import {
  interestFilterFields,
  type InterestConfigType,
  type Location,
  type PropertyFiltersCoreType,
  type PropertyFiltersType,
} from '@aikoa/common'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import Animated, { LinearTransition } from 'react-native-reanimated'
import { InterestFilterEditor } from '~/components/interests/InterestFilterEditor'
import {
  InterestFilterSummary,
  type InterestFilterSummaryContext,
} from '~/components/interests/InterestFilterSummary'
import { cn } from '~/lib/utils'

type InterestFilterConfiguratorProps = {
  filters: PropertyFiltersType
  locations: Location[]
  onChangeFilters: Dispatch<SetStateAction<PropertyFiltersType>>
  onChangeLocations: Dispatch<SetStateAction<Location[]>>
  renderLocationsEditor: (props: {
    locations: Location[]
    onChangeLocations: (locations: Location[]) => void
  }) => React.ReactNode
  context?: InterestFilterSummaryContext
  includeLocationsByDefault?: boolean
  initialEditor?: InterestConfigType | null
  deriveFilters?: (editor: InterestConfigType) => Partial<PropertyFiltersType> | null
  onActivatedFiltersChange?: (filters: InterestConfigType[]) => void
  className?: string
}

export function InterestFilterConfigurator({
  filters,
  locations,
  onChangeFilters,
  onChangeLocations,
  renderLocationsEditor,
  context = 'own',
  includeLocationsByDefault = true,
  initialEditor,
  deriveFilters,
  onActivatedFiltersChange,
  className,
}: InterestFilterConfiguratorProps) {
  const existingFilters = useMemo(() => {
    const activeFilters = new Set<InterestConfigType>()

    if (includeLocationsByDefault || locations.length > 0) {
      activeFilters.add('locations')
    }

    const filterEntries = Object.entries(interestFilterFields) as [PropertyFiltersCoreType, Array<keyof PropertyFiltersType>][]

    for (const [filterName, fieldKeys] of filterEntries) {
      for (const fieldKey of fieldKeys) {
        if (filters[fieldKey] !== undefined) {
          activeFilters.add(filterName)
          break
        }
      }
    }

    return Array.from(activeFilters)
  }, [filters, includeLocationsByDefault, locations.length])

  const [openEditor, setOpenEditor] = useState<InterestConfigType | null>(() => {
    if (initialEditor !== undefined) {
      return initialEditor
    }

    return includeLocationsByDefault ? 'locations' : null
  })

  const [activatedFilters, setActivatedFilters] = useState<Set<InterestConfigType>>(
    () => new Set(existingFilters)
  )

  useEffect(() => {
    setActivatedFilters((previous) => {
      const next = new Set(previous)

      for (const filterName of existingFilters) {
        next.add(filterName)
      }

      return next
    })
  }, [existingFilters])

  useEffect(() => {
    if (onActivatedFiltersChange) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
      onActivatedFiltersChange(Array.from(activatedFilters))
    }
  }, [activatedFilters, onActivatedFiltersChange])

  const getInterestFilterIsSet = useCallback(
    (editor: InterestConfigType) => {
      if (!editor || !(editor in interestFilterFields)) {
        return false
      }

      const fields = interestFilterFields[editor as PropertyFiltersCoreType] ?? []

      for (const fieldKey of fields) {
        if (filters[fieldKey] !== undefined) {
          return true
        }
      }

      return false
    },
    [filters]
  )

  const handleLocationsChange = useCallback(
    (nextLocations: Location[]) => {
      const filteredLocations = nextLocations.filter((location) => !!location.coordinates)

      onChangeLocations(filteredLocations)
    },
    [onChangeLocations]
  )

  const handleOpenEditor = useCallback(
    (editor: InterestConfigType | undefined) => {
      if (!editor) {
        setOpenEditor(null)
        return
      }

      if (!getInterestFilterIsSet(editor) && deriveFilters) {
        const derivedFilters = deriveFilters(editor)

        if (derivedFilters) {
          onChangeFilters((currentFilters) => ({
            ...currentFilters,
            ...derivedFilters,
          }))
        }
      }

      setActivatedFilters((previous) => new Set(previous).add(editor))
      setOpenEditor(editor)
    },
    [deriveFilters, getInterestFilterIsSet, onChangeFilters]
  )

  const renderEditor = useCallback(
    (editor: InterestConfigType) => (
      <Animated.View
        key={`editor-${editor}`}
        className="relative z-50 my-2 w-full gap-6 rounded-content bg-muted px-4 py-6 sm:px-8"
        layout={LinearTransition.duration(150)}>
        {editor === 'locations' ? (
          renderLocationsEditor({
            locations,
            onChangeLocations: handleLocationsChange,
          })
        ) : (
          <InterestFilterEditor
            editor={editor}
            filters={filters}
            onChange={(nextFilters) => onChangeFilters(nextFilters)}
          />
        )}
      </Animated.View>
    ),
    [filters, handleLocationsChange, locations, onChangeFilters, renderLocationsEditor]
  )

  return (
    <InterestFilterSummary
      className={cn('relative isolate z-10', className)}
      filters={filters}
      locations={locations}
      selectedEditor={openEditor ?? undefined}
      onOpenEditor={handleOpenEditor}
      activatedFilters={Array.from(activatedFilters)}
      renderEditor={renderEditor}
      context={context}
    />
  )
}
